import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper.js"

// Keep in sync with the modal transition duration in assistant.scss!
const MODAL_ANIMATION_MS = 250

const CARD_BRAND_ICONS = {
  visa: "icon-lasso-cc-visa",
  mastercard: "icon-lasso-cc-mastercard",
  "american express": "icon-lasso-cc-american-express",
  amex: "icon-lasso-cc-american-express",
  discover: "icon-lasso-cc-discover"
}

const CARD_FORM_OWNER_LABELS = {
  user: "Personal Payment Method",
  organization: "Organization's Payment Method"
}

const EYE_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>' +
  '<circle cx="12" cy="12" r="3"></circle></svg>'

export default class extends Controller {
  static get targets() {
    return [
      "emptyState", "transcript", "input", "privacyNote",
      "productsModal", "productsGrid",
      "footerThumb", "footerPrice", "footerMeta", "footerName", "footerSelect",
      "designSidebar", "sidebarLogo", "sidebarImage", "sidebarMessage", "sidebarFrom",
      "imageModal", "imageGrid", "imageHeading", "giphySearch", "giphyQuery",
      "libraryTab", "stockTab", "giphyTab",
      "editMessageModal", "editMessageInput",
      "logoModal", "logoGrid", "logoUpload", "logoUploadButton",
      "checkoutSidebar", "checkoutCollection", "checkoutDetails", "checkoutQty",
      "checkoutPriceLabel", "checkoutSubtotal", "checkoutShippingLabel", "checkoutShipping", "checkoutTotal",
      "checkoutCredits", "checkoutOrderTotal", "checkoutAvailableCredits", "checkoutBalanceAfter",
      "checkoutInvoiceNote",
      "checkoutPayment", "checkoutPaymentTitle", "checkoutPaymentBody", "checkoutMethods", "checkoutCardAmount",
      "checkoutCardForm", "checkoutCardFormOwner", "checkoutCardErrors", "checkoutCardSave",
      "successSidebar", "successSubtitle",
      "emailPreviewModal", "emailPreviewFrame",
      "redemptionPreviewModal", "redemptionPhoneFrame", "redemptionDesktopFrame"
    ]
  }

  connect() {
    this.selectedDesignId = null
    this.pendingClears = new Set()
    this.selectedTone = null
    this.sidebarOpen = false
    this.checkoutOpen = false
    this.initialPlaceholder = this.inputTarget.placeholder
    this.loadHistory()
  }

  // Restores the server-held conversation (shared with the chat drawer) so a
  // refresh or navigation doesn't drop the user back on the intro screen.
  async loadHistory() {
    try {
      const response = await fetch("/chatbot", RequestHelper.defaultRequestOptions())
      if (!response.ok) {
        return
      }

      const data = await response.json()
      const messages = data.messages || []
      if (messages.length === 0) {
        return
      }

      this.activateChat()
      messages.forEach((message) => {
        const sender = message.role === "assistant" ? "bot" : "user"
        this.appendMessage(message.content, sender)
      })
      await this.restorePanel(data.panel)
    } catch (e) {
      // Leave the intro screen in place if history can't be loaded.
    }
  }

  async restorePanel(panel) {
    if (panel === "checkout") {
      this.showCheckout()
      await this.refreshCheckout()
    } else if (panel === "design") {
      this.showDesignSidebar()
      await this.refreshEmailDesign()
    }
  }

  async send(event) {
    event.preventDefault()

    const message = this.inputTarget.value.trim()
    if (message === "") {
      return
    }

    this.inputTarget.value = ""
    await this.deliver(message)
  }

  async deliver(message) {
    this.activateChat()
    this.appendMessage(message, "user")
    this.showThinking()

    const options = RequestHelper.defaultPostOptions()
    options.headers["Content-Type"] = "application/json"

    const body = { message: message }
    if (this.selectedDesignId) {
      body.design_id = this.selectedDesignId
    }
    if (this.pendingClears.size > 0) {
      body.clear_selections = Array.from(this.pendingClears)
    }
    options.body = JSON.stringify(body)

    const response = await fetch("/chatbot", options)
    this.removeThinking()

    if (!response.ok) {
      return
    }

    const data = await response.json()
    if (data.restart) {
      this.resetDesignState()
      this.resetCheckoutState()
    }
    if (data.message) {
      await this.typeMessage(data.message)
    }
    if (data.complete && data.action_type === "gift" && data.action_success) {
      this.showSuccess(data)
    }
    if (this.sidebarOpen) {
      await this.refreshEmailDesign()
    }
    if (this.checkoutOpen) {
      await this.refreshCheckout()
    }
  }

  activateChat() {
    this.emptyStateTarget.classList.add("hidden")
    this.transcriptTarget.classList.remove("hidden")
    this.privacyNoteTarget.classList.remove("hidden")
    this.inputTarget.placeholder = "Type a message..."
  }

  appendMessage(text, sender) {
    const message = document.createElement("div")
    message.className = `assistant-message assistant-message-${sender}`
    message.textContent = text
    this.transcriptTarget.appendChild(message)
    this.scrollToLatest()
    return message
  }

  showThinking() {
    const indicator = document.createElement("div")
    indicator.className = "assistant-message assistant-message-bot assistant-typing"
    indicator.innerHTML = "<span></span><span></span><span></span>"
    this.transcriptTarget.appendChild(indicator)
    this.scrollToLatest()
  }

  removeThinking() {
    const indicator = this.transcriptTarget.querySelector(".assistant-typing")
    if (indicator) {
      indicator.remove()
    }
  }

  // Types the reply out at the chat drawer's pace; long replies render at
  // once, matching the drawer's cutoff. UI markers embedded in the reply are
  // stripped from display and rendered as their widgets afterwards.
  async typeMessage(text) {
    const pricePickerMatch = text.match(/\[PRICE_PICKER(?::(domestic|international))?\]/)
    const collectionNames = [...new Set(
      [...text.matchAll(/\[COLLECTION_PREVIEW:([^\]]+)\]/g)].map((match) => match[1].trim())
    )]
    const cleanText = this.stripMarkers(text)

    const message = this.appendMessage("", "bot")

    if (cleanText.length > 300) {
      message.innerHTML = this.formatBotMessage(cleanText)
      this.scrollToLatest()
    } else {
      for (let index = 1; index <= cleanText.length; index++) {
        message.innerHTML = this.formatBotMessage(cleanText.slice(0, index))
        this.scrollToLatest()
        await new Promise((resolve) => setTimeout(resolve, 15))
      }
    }

    if (pricePickerMatch) {
      await this.appendPriceButtons(pricePickerMatch[1] === "international")
    }
    if (collectionNames.length > 0) {
      await this.appendCollectionCards(collectionNames)
    }
    if (text.includes("[DESIGN_PICKER]")) {
      this.showDesignSidebar()
    }
    if (text.includes("[GIFT_PREVIEW]") || text.includes("[PAYMENT_PICKER]")) {
      this.showCheckout()
    }
  }

  stripMarkers(text) {
    const marker = "\\[(?:DESIGN_PICKER|PAYMENT_PICKER|CONTACT_PICKER|GIFT_PREVIEW|PRICE_PICKER(?::(?:domestic|international))?|COLLECTION_PREVIEW:[^\\]]+)\\]"
    return text
      .replace(new RegExp(`^[^\\S\\n]*(?:-[^\\S\\n]*)?${marker}[^\\S\\n]*(?:\\n|$)`, "gm"), "")
      .replace(new RegExp(`[^\\S\\n]*${marker}[^\\S\\n]*`, "g"), " ")
      .replace(/[^\S\n]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  }

  formatBotMessage(text) {
    return this.escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br>")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  async appendPriceButtons(international) {
    try {
      const response = await fetch(`/chatbot/price_points?international=${international}`, RequestHelper.defaultRequestOptions())
      const pricePoints = await response.json()

      const row = document.createElement("div")
      row.className = "assistant-selectors"

      pricePoints.forEach((point) => {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "assistant-price-btn"
        btn.textContent = point.price
        btn.addEventListener("click", () => this.deliver(point.price))
        row.appendChild(btn)
      })

      this.transcriptTarget.appendChild(row)
      this.scrollToLatest()
    } catch (e) {
      // The user can still type their budget.
    }
  }

  async appendCollectionCards(names) {
    try {
      const params = new URLSearchParams()
      names.forEach((name) => params.append("names[]", name))
      const response = await fetch(`/chatbot/collections?${params}`, RequestHelper.defaultRequestOptions())
      const cards = await response.json()

      const grid = document.createElement("div")
      grid.className = "assistant-collection-cards"
      cards.forEach((card) => grid.appendChild(this.buildCollectionCard(card)))

      this.transcriptTarget.appendChild(grid)
      this.scrollToLatest()
    } catch (e) {
      // The collection names remain usable as plain conversation.
    }
  }

  buildCollectionCard(card, selectable = true) {
    const el = document.createElement("div")
    el.className = "assistant-collection-card"

    if (card.image_url) {
      const img = document.createElement("img")
      img.src = card.image_url
      img.alt = card.name
      // A broken cover image reads worse than none.
      img.addEventListener("error", () => img.remove())
      el.appendChild(img)
    }

    const header = document.createElement("div")
    header.className = "assistant-collection-card-header"

    const price = document.createElement("span")
    price.className = "assistant-collection-card-price"
    price.textContent = card.price
    header.appendChild(price)

    const preview = document.createElement("button")
    preview.type = "button"
    preview.className = "assistant-collection-card-preview"
    preview.innerHTML = `${EYE_ICON_SVG} Preview`
    preview.addEventListener("click", () => this.openProducts(card, selectable))
    header.appendChild(preview)

    el.appendChild(header)

    const meta = document.createElement("div")
    meta.className = "assistant-collection-card-meta"
    meta.textContent = this.productCountLabel(card.product_count)
    el.appendChild(meta)

    const name = document.createElement("div")
    name.className = "assistant-collection-card-name"
    name.textContent = card.name
    el.appendChild(name)

    return el
  }

  async openProducts(card, selectable) {
    this.previewedCard = card
    this.footerSelectTarget.classList.toggle("hidden", !selectable)

    if (card.image_url) {
      this.footerThumbTarget.src = card.image_url
      this.footerThumbTarget.classList.remove("hidden")
      this.footerThumbTarget.onerror = () => this.footerThumbTarget.classList.add("hidden")
    } else {
      this.footerThumbTarget.classList.add("hidden")
    }
    this.footerPriceTarget.textContent = card.price
    this.footerMetaTarget.textContent =
      `${card.international ? "International" : "US (Domestic)"} | ${this.productCountLabel(card.product_count)}`
    this.footerNameTarget.textContent = card.name

    this.renderProductSkeletons()
    this.openModal()

    try {
      const response = await fetch(`/chatbot/collection_products?collection=${encodeURIComponent(card.name)}`, RequestHelper.defaultRequestOptions())
      const data = await response.json()

      const columns = this.resetProductColumns()

      const products = data.products || []
      products.forEach((product, index) => {
        const tile = document.createElement("div")
        tile.className = "assistant-product-tile"

        if (product.image_url) {
          const img = document.createElement("img")
          img.src = product.image_url
          img.alt = product.name
          tile.appendChild(img)
        }

        const name = document.createElement("div")
        name.className = "assistant-product-tile-name"
        name.textContent = product.name
        tile.appendChild(name)

        columns[index % columns.length].appendChild(tile)
      })
    } catch (e) {
      this.productsGridTarget.textContent = "Products couldn't be loaded."
    }
  }

  // Placeholder tiles shown while the product list loads, so the grid area doesn't pop from empty to full.
  renderProductSkeletons() {
    const columns = this.resetProductColumns()
    for (let i = 0; i < 6; i++) {
      const tile = document.createElement("div")
      tile.className = "assistant-product-tile assistant-product-skeleton"
      tile.innerHTML = '<div class="assistant-skeleton-image"></div><div class="assistant-skeleton-line"></div>'
      columns[i % columns.length].appendChild(tile)
    }
  }

  resetProductColumns() {
    this.productsGridTarget.innerHTML = ""
    return [0, 1, 2].map(() => {
      const column = document.createElement("div")
      column.className = "assistant-products-column"
      this.productsGridTarget.appendChild(column)
      return column
    })
  }

  openModal() {
    this.openOverlay(this.productsModalTarget)
  }

  closeProducts() {
    this.closeOverlay(this.productsModalTarget)
  }

  openOverlay(modal) {
    clearTimeout(modal.hideTimer)
    modal.classList.remove("hidden")
    requestAnimationFrame(() => {
      requestAnimationFrame(() => modal.classList.add("is-open"))
    })
  }

  closeOverlay(modal) {
    modal.classList.remove("is-open")
    modal.hideTimer = setTimeout(() => modal.classList.add("hidden"), MODAL_ANIMATION_MS)
  }

  selectCollection() {
    if (!this.previewedCard) {
      return
    }

    const name = this.previewedCard.name
    this.closeProducts()
    this.deliver(`I'd like the "${name}" collection`)
  }

  productCountLabel(count) {
    return `${count} ${count === 1 ? "Product" : "Products"}`
  }

  showDesignSidebar() {
    this.designSidebarTarget.classList.remove("hidden")
    this.sidebarOpen = true
  }

  resetDesignState() {
    this.designSidebarTarget.classList.add("hidden")
    this.sidebarOpen = false
    this.selectedDesignId = null
    this.pendingClears.clear()
  }

  async refreshEmailDesign() {
    try {
      const response = await fetch("/chatbot/email_design", RequestHelper.defaultRequestOptions())
      this.renderEmailDesign(await response.json())
    } catch (e) {
      // The sidebar keeps showing its last known preview.
    }
  }

  renderEmailDesign(data) {
    this.toggleSidebarImage(this.sidebarLogoTarget, data.logo_url)
    this.toggleSidebarImage(this.sidebarImageTarget, data.design && data.design.image_url)
    if (data.design) {
      this.sidebarImageTarget.alt = data.design.name
    }
    this.sidebarMessageTarget.textContent = data.message || ""
    this.sidebarFromTarget.textContent = data.from || ""
  }

  toggleSidebarImage(img, url) {
    if (url) {
      img.src = url
      img.classList.remove("hidden")
    } else {
      img.classList.add("hidden")
    }
  }

  showCheckout() {
    this.designSidebarTarget.classList.add("hidden")
    this.sidebarOpen = false
    this.checkoutSidebarTarget.classList.remove("hidden")
    this.checkoutOpen = true
  }

  resetCheckoutState() {
    this.checkoutSidebarTarget.classList.add("hidden")
    this.checkoutOpen = false
    this.checkoutCollectionTarget.innerHTML = ""
    this.closeCardForm()
  }

  showSuccess(data) {
    this.resetCheckoutState()
    const count = data.recipient_count || 1
    const verb = data.scheduled_for_later ? "scheduled" : "sent"
    this.successSubtitleTarget.textContent = `You successfully ${verb} ${count} ${count === 1 ? "gift" : "gifts"}`
    this.successSidebarTarget.classList.remove("hidden")
  }

  closeSuccess() {
    this.successSidebarTarget.classList.add("hidden")
  }

  async startOver() {
    this.closeSuccess()
    this.resetDesignState()
    this.resetCheckoutState()

    this.transcriptTarget.innerHTML = ""
    this.transcriptTarget.classList.add("hidden")
    this.privacyNoteTarget.classList.add("hidden")
    this.emptyStateTarget.classList.remove("hidden")
    this.inputTarget.placeholder = this.initialPlaceholder

    try {
      await fetch("/chatbot/reset", RequestHelper.defaultDeleteOptions())
    } catch (e) {
      // The next turn still works; it just picks up the old transcript.
    }
  }

  async refreshCheckout() {
    try {
      const response = await fetch("/chatbot/checkout", RequestHelper.defaultRequestOptions())
      this.renderCheckout(await response.json())
    } catch (e) {
      // The sidebar keeps showing its last known details.
    }
  }

  renderCheckout(data) {
    if (!data.collection) {
      this.resetCheckoutState()
      return
    }

    this.checkoutDetailsTarget.classList.toggle("hidden", this.cardFormOpen())
    this.checkoutCollectionTarget.innerHTML = ""
    this.checkoutCollectionTarget.appendChild(this.buildCollectionCard(data.collection, false))

    const purchase = data.purchase
    this.checkoutQtyTargets.forEach((cell) => { cell.textContent = purchase.quantity })
    this.checkoutPriceLabelTarget.textContent = `${purchase.price_per_gift} Collection`
    this.checkoutSubtotalTarget.textContent = purchase.subtotal
    this.checkoutShippingLabelTarget.textContent = `${purchase.shipping_per_gift} shipping`
    this.checkoutShippingTarget.textContent = purchase.shipping
    this.checkoutTotalTarget.textContent = purchase.total

    this.checkoutCreditsTarget.classList.toggle("hidden", !purchase.credits)
    if (purchase.credits) {
      this.checkoutOrderTotalTarget.textContent = purchase.total
      this.checkoutAvailableCreditsTarget.textContent = purchase.credits.available
      this.checkoutBalanceAfterTarget.textContent = purchase.credits.balance_after
    }
    this.checkoutInvoiceNoteTarget.classList.toggle("hidden", !purchase.invoiceable)
    this.renderCheckoutPayment(purchase, data.payment)
  }

  renderCheckoutPayment(purchase, payment) {
    this.checkoutPaymentTarget.classList.toggle("hidden", !payment)
    if (!payment) {
      return
    }

    const hasCards = payment.methods.organization.length + payment.methods.user.length > 0
    this.checkoutPaymentTitleTarget.textContent = purchase.credits ?
      "Not enough credits to cover this order." :
      "No credits available!"
    this.checkoutPaymentBodyTarget.textContent = hasCards ?
      `${purchase.card_amount} will be charged to the card selected below.` :
      `Add a card to pay the ${purchase.card_amount} due for this order.`
    this.checkoutCardAmountTarget.textContent = purchase.card_amount

    this.checkoutMethodsTargets.forEach((list) => {
      const owner = list.dataset.owner
      list.innerHTML = ""

      const methods = payment.methods[owner]
      if (methods.length === 0) {
        list.appendChild(this.buildEmptyPaymentMethods(owner, payment.can_add_card))
      } else {
        methods.forEach((pm) => list.appendChild(this.buildPaymentMethod(pm, pm.id === payment.selected_id)))
      }
    })
  }

  buildPaymentMethod(pm, selected) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "assistant-checkout-method"
    btn.classList.toggle("is-selected", selected)
    btn.setAttribute("aria-pressed", selected)
    btn.dataset.id = pm.id

    const radio = document.createElement("span")
    radio.className = "assistant-checkout-method-radio"
    btn.appendChild(radio)

    const icon = document.createElement("i")
    icon.className = this.paymentMethodIcon(pm)
    btn.appendChild(icon)

    const details = document.createElement("span")
    details.className = "assistant-checkout-method-details"

    if (pm.name) {
      const name = document.createElement("span")
      name.className = "assistant-checkout-method-name"
      name.textContent = pm.name
      details.appendChild(name)
    }

    const label = document.createElement("span")
    label.className = "assistant-checkout-method-label"
    label.textContent = this.paymentMethodLabel(pm)
    details.appendChild(label)

    if (pm.expiration) {
      const expiration = document.createElement("span")
      expiration.className = "assistant-checkout-method-expiration"
      expiration.textContent = pm.expiration
      details.appendChild(expiration)
    }

    btn.appendChild(details)
    btn.addEventListener("click", () => this.selectPaymentMethod(pm))
    return btn
  }

  buildEmptyPaymentMethods(owner, canAdd) {
    const el = document.createElement("div")
    el.className = "assistant-checkout-method-empty"

    const icon = document.createElement("i")
    icon.className = "icon-lasso-credit-card"
    el.appendChild(icon)

    const text = document.createElement("span")
    text.textContent = "No payment method added yet."
    el.appendChild(text)

    if (canAdd) {
      const add = document.createElement("button")
      add.type = "button"
      add.className = "assistant-checkout-add-card"
      add.textContent = "Add new card"
      add.addEventListener("click", () => this.openCardForm(owner))
      el.appendChild(add)
    }

    return el
  }

  paymentMethodBrand(pm) {
    if (pm.brand) {
      return pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
    }
    return pm.type === "bank" ? "Bank account" : "Card"
  }

  paymentMethodLabel(pm) {
    return `${this.paymentMethodBrand(pm)} \u2022\u2022\u2022\u2022 ${pm.last4}`
  }

  paymentMethodIcon(pm) {
    if (pm.type === "bank") {
      return "icon-lasso-bill"
    }
    return CARD_BRAND_ICONS[(pm.brand || "").toLowerCase()] || "icon-lasso-credit-card"
  }

  async selectPaymentMethod(pm) {
    const options = RequestHelper.defaultPatchOptions()
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify({ payment_method_id: pm.id })

    try {
      const response = await fetch("/chatbot/payment_methods/select", options)
      if (!response.ok) {
        throw new Error("Payment method not available")
      }
    } catch (e) {
      // The next checkout refresh shows whatever the server still has
      return
    }

    this.markSelectedPaymentMethod(pm.id)
    await this.deliver(`I've selected my payment method: ${this.paymentMethodLabel(pm)}`)
  }

  markSelectedPaymentMethod(id) {
    this.checkoutPaymentTarget.querySelectorAll(".assistant-checkout-method").forEach((btn) => {
      const selected = btn.dataset.id === String(id)
      btn.classList.toggle("is-selected", selected)
      btn.setAttribute("aria-pressed", selected)
    })
  }

  openCardForm(owner) {
    this.cardFormOwner = owner
    this.checkoutCardFormOwnerTarget.textContent = CARD_FORM_OWNER_LABELS[owner]
    this.resetCardForm()
    this.checkoutDetailsTarget.classList.add("hidden")
    this.checkoutCardFormTarget.classList.remove("hidden")
    this.cardField("name").focus()
  }

  cancelCardForm() {
    this.closeCardForm()
  }

  cardFormOpen() {
    return !this.checkoutCardFormTarget.classList.contains("hidden")
  }

  closeCardForm() {
    if (!this.cardFormOpen()) {
      return
    }
    this.checkoutCardFormTarget.classList.add("hidden")
    this.checkoutDetailsTarget.classList.remove("hidden")
  }

  resetCardForm() {
    this.checkoutCardFormTarget.querySelectorAll("input").forEach((input) => { input.value = "" })
    this.checkoutCardErrorsTarget.textContent = ""
    this.validateCardForm()
  }

  cardField(name) {
    return this.checkoutCardFormTarget.querySelector(`[data-field="${name}"]`)
  }

  cardValues() {
    return ["name", "number", "exp", "cvc"].reduce((values, field) => {
      values[field] = this.cardField(field).value.trim()
      return values
    }, {})
  }

  validateCardForm() {
    this.checkoutCardSaveTarget.disabled = Object.values(this.cardValues()).some((value) => value === "")
  }

  async saveCard(event) {
    event.preventDefault()

    const { name, number, exp, cvc } = this.cardValues()
    const [expMonth, expYear] = exp.replace(/\s/g, "").split("/")
    this.checkoutCardErrorsTarget.textContent = ""

    if (!expMonth || !expYear) {
      this.checkoutCardErrorsTarget.textContent = "Invalid expiration format. Use MM / YY."
      return
    }

    const save = this.checkoutCardSaveTarget
    save.disabled = true
    save.textContent = "Saving..."

    try {
      const token = await this.stripeCardToken({
        name: name,
        number: number,
        exp_month: parseInt(expMonth, 10),
        exp_year: parseInt(expYear, 10),
        cvc: cvc
      })

      const options = RequestHelper.defaultPostOptions()
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify({
        payment_method: {
          stripe_token: token.id,
          stripe_name: name,
          stripe_last4: token.card.last4,
          owner_type: this.cardFormOwner
        }
      })

      const response = await fetch("/chatbot/payment_methods", options)
      const card = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((card.errors || []).join(", ") || "Failed to add card.")
      }

      this.closeCardForm()
      await this.refreshCheckout()
      await this.deliver(`I've selected my payment method: ${this.paymentMethodLabel(card)}`)
    } catch (error) {
      this.checkoutCardErrorsTarget.textContent = error.message
    } finally {
      save.textContent = "Save Card"
      save.disabled = false
    }
  }

  stripeCardToken(card) {
    return new Promise((resolve, reject) => {
      Stripe.card.createToken(card, (status, response) => {
        if (response.error) {
          reject(new Error(response.error.message))
        } else {
          resolve(response)
        }
      })
    })
  }

  openEmailPreview() {
    this.emailPreviewFrameTarget.src = this.previewUrl("/chatbot/email_preview")
    this.openOverlay(this.emailPreviewModalTarget)
  }

  closeEmailPreview() {
    this.closePreview(this.emailPreviewModalTarget, [this.emailPreviewFrameTarget])
  }

  openRedemptionPreview() {
    this.openOverlay(this.redemptionPreviewModalTarget)
    this.redemptionPhoneFrameTarget.src = this.previewUrl("/chatbot/redemption_preview")
    if (this.redemptionDesktopFrameTarget.offsetParent !== null) {
      this.redemptionDesktopFrameTarget.src = this.previewUrl("/chatbot/redemption_preview")
    }
  }

  closeRedemptionPreview() {
    this.closePreview(this.redemptionPreviewModalTarget, [this.redemptionPhoneFrameTarget, this.redemptionDesktopFrameTarget])
  }

  previewUrl(path) {
    const params = new URLSearchParams()
    if (this.selectedDesignId) {
      params.set("design_id", this.selectedDesignId)
    }
    const query = params.toString()
    return query === "" ? path : `${path}?${query}`
  }

  closePreview(modal, frames) {
    this.closeOverlay(modal)
    setTimeout(() => {
      frames.forEach((frame) => {
        frame.removeAttribute("src")
        if (frame.contentWindow) {
          frame.contentWindow.location.replace("about:blank")
        }
      })
    }, MODAL_ANIMATION_MS)
  }

  disarmPreviewLinks(event) {
    const doc = event.target.contentDocument
    if (!doc) {
      return
    }
    doc.addEventListener("click", (click) => {
      if (click.target.closest("a")) {
        click.preventDefault()
        click.stopPropagation()
      }
    }, true)
  }

  openImageModal() {
    this.openOverlay(this.imageModalTarget)
    this.showStockTab()
  }

  closeImageModal() {
    this.closeOverlay(this.imageModalTarget)
  }

  showStockTab() {
    this.activateImageTab(this.stockTabTarget)
    this.loadDesignGrid("/chatbot/designs")
  }

  showLibraryTab() {
    this.activateImageTab(this.libraryTabTarget)
    this.loadDesignGrid("/chatbot/designs?tab=library")
  }

  showGiphyTab() {
    this.activateImageTab(this.giphyTabTarget)
    this.loadGiphyGrid()
  }

  activateImageTab(tab) {
    const tabs = [this.libraryTabTarget, this.stockTabTarget, this.giphyTabTarget]
    tabs.forEach((button) => button.classList.toggle("is-active", button === tab))
    this.giphySearchTarget.classList.toggle("hidden", tab !== this.giphyTabTarget)
    this.imageHeadingTarget.classList.toggle("hidden", tab !== this.stockTabTarget)
  }

  async loadDesignGrid(url) {
    this.imageGridTarget.innerHTML = ""
    try {
      const response = await fetch(url, RequestHelper.defaultRequestOptions())
      const designs = await response.json()

      designs.forEach((design) => {
        const tile = document.createElement("button")
        tile.type = "button"
        tile.className = "assistant-image-tile"
        tile.setAttribute("aria-label", design.name)

        const img = document.createElement("img")
        img.src = design.thumbnail_url
        img.alt = design.name
        img.addEventListener("error", () => img.remove())
        tile.appendChild(img)

        tile.addEventListener("click", () => this.selectDesign(design))
        this.imageGridTarget.appendChild(tile)
      })
    } catch (e) {
      this.imageGridTarget.textContent = "Images couldn't be loaded."
    }
  }

  async searchGiphy(event) {
    event.preventDefault()
    await this.loadGiphyGrid(this.giphyQueryTarget.value.trim())
  }

  async loadGiphyGrid(query = "") {
    this.imageGridTarget.innerHTML = ""
    try {
      const params = query === "" ? "" : `?query=${encodeURIComponent(query)}`
      const response = await fetch(`/chatbot/giphy${params}`, RequestHelper.defaultRequestOptions())
      const gifs = await response.json()

      gifs.forEach((gif) => {
        const tile = document.createElement("button")
        tile.type = "button"
        tile.className = "assistant-image-tile assistant-image-tile-gif"

        const img = document.createElement("img")
        img.src = gif.preview_url
        img.alt = "GIF"
        tile.appendChild(img)

        tile.addEventListener("click", () => this.selectGif(gif))
        this.imageGridTarget.appendChild(tile)
      })
    } catch (e) {
      this.imageGridTarget.textContent = "Giphy couldn't be loaded."
    }
  }

  selectDesign(design) {
    this.selectedDesignId = design.id
    this.pendingClears.delete("design_id")
    this.toggleSidebarImage(this.sidebarImageTarget, design.thumbnail_url)
    this.closeImageModal()
    this.deliver(`I picked the "${design.name}" image`)
  }

  async selectGif(gif) {
    const options = RequestHelper.defaultPostOptions()
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify({
      external_url: gif.external_url,
      dimension_width: gif.width,
      dimension_height: gif.height,
      dimension_string: gif.dimension_string
    })

    try {
      const response = await fetch("/chatbot/giphy", options)
      if (!response.ok) {
        return
      }
      const design = await response.json()

      this.selectedDesignId = design.id
      this.pendingClears.delete("design_id")
      this.toggleSidebarImage(this.sidebarImageTarget, gif.external_url)
      this.closeImageModal()
      this.deliver("I picked a GIF for the email image")
    } catch (e) {
      // The modal stays open so the user can pick again.
    }
  }

  openLogoModal() {
    this.openOverlay(this.logoModalTarget)
    this.loadLogoGrid()
  }

  closeLogoModal() {
    this.closeOverlay(this.logoModalTarget)
  }

  async loadLogoGrid() {
    this.logoGridTarget.innerHTML = ""
    try {
      const response = await fetch("/chatbot/logos", RequestHelper.defaultRequestOptions())
      const data = await response.json()

      this.logoUploadButtonTarget.classList.toggle("hidden", !data.can_upload)
      data.logos.forEach((logo) => this.logoGridTarget.appendChild(this.buildLogoTile(logo)))
    } catch (e) {
      this.logoGridTarget.textContent = "Logos couldn't be loaded."
    }
  }

  buildLogoTile(logo) {
    const tile = document.createElement("button")
    tile.type = "button"
    tile.className = "assistant-logo-tile"
    tile.classList.toggle("is-selected", logo.selected)
    tile.setAttribute("aria-label", logo.name)
    tile.setAttribute("aria-pressed", logo.selected)

    const check = document.createElement("span")
    check.className = "assistant-logo-tile-check"
    check.innerHTML = '<i class="icon-lasso-check-circle"></i>'
    tile.appendChild(check)

    const img = document.createElement("img")
    img.src = logo.image_url
    img.alt = logo.name
    img.addEventListener("error", () => img.remove())
    tile.appendChild(img)

    tile.addEventListener("click", () => this.selectLogo(logo))
    return tile
  }

  selectLogo(logo) {
    return this.updateLogoSelection({ logo_id: logo.id })
  }

  hideLogo() {
    return this.updateLogoSelection({ hidden: true })
  }

  // The logo pick is stored server-side rather than sent as a chat message:
  // the assistant never asks about the logo, so there's nothing to answer.
  async updateLogoSelection(selection) {
    const options = RequestHelper.defaultPatchOptions()
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify(selection)

    try {
      const response = await fetch("/chatbot/logos/select", options)
      if (!response.ok) {
        return
      }
      const data = await response.json()
      this.toggleSidebarImage(this.sidebarLogoTarget, data.logo_url)
      this.closeLogoModal()
    } catch (e) {
      // The modal stays open so the user can try again.
    }
  }

  pickLogoFile() {
    this.logoUploadTarget.click()
  }

  async uploadLogo(event) {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const options = RequestHelper.defaultPostOptions()
    options.body = new FormData()
    options.body.append("image", file)

    try {
      const response = await fetch("/chatbot/logos", options)
      if (!response.ok) {
        return
      }
      const logo = await response.json()
      this.toggleSidebarImage(this.sidebarLogoTarget, logo.image_url)
      this.closeLogoModal()
    } catch (e) {
      // The modal stays open so the user can try again.
    } finally {
      // Otherwise re-picking the same file wouldn't fire change.
      event.target.value = ""
    }
  }

  openEditMessage() {
    this.openOverlay(this.editMessageModalTarget)
  }

  closeEditMessage() {
    this.closeOverlay(this.editMessageModalTarget)
  }

  selectTone(event) {
    const pill = event.currentTarget
    const alreadySelected = pill.classList.contains("is-selected")

    this.tonePills().forEach((button) => button.classList.remove("is-selected"))
    pill.classList.toggle("is-selected", !alreadySelected)
    this.selectedTone = alreadySelected ? null : pill.textContent.trim()
  }

  submitEditMessage() {
    const details = this.editMessageInputTarget.value.trim()
    if (!this.selectedTone && details === "") {
      return
    }

    const parts = []
    if (this.selectedTone) {
      parts.push(`use a ${this.selectedTone} tone of voice`)
    }
    if (details !== "") {
      parts.push(details)
    }
    const message = `Please update the message: ${parts.join(". ")}`

    this.editMessageInputTarget.value = ""
    this.tonePills().forEach((button) => button.classList.remove("is-selected"))
    this.selectedTone = null

    this.closeEditMessage()
    this.deliver(message)
  }

  tonePills() {
    return this.editMessageModalTarget.querySelectorAll(".assistant-tone-pill")
  }

  // The transcript is the chat's scroll container; keep it pinned to the
  // newest content as messages append and type out.
  scrollToLatest() {
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }
}
